/**
 * The module's degradation curve, as the manufacturer warrants it.
 *
 * MIRRORS pv_core/degradation.py. If the rule changes there, change it here:
 * a panel that disagrees with the engine about how much energy a plant makes in
 * year 20 is worse than a panel that shows nothing.
 *
 * Stored as RETENTION per year -- percent of nameplate remaining, year 1 first
 * -- because that is what a warranty table prints. Reading a number off a page
 * beats differencing it in your head.
 *
 * The two halves of the curve are consumed in different places, and the split
 * is the whole point:
 *
 *   - the FIRST-YEAR drop is light-induced degradation, and goes into the
 *     hourly simulation as the DC loss `pvLosses.dc.lid` -- a term that was
 *     already there, already defaulting to 1%, and is the same physical effect
 *     under its proper name;
 *   - years 2 and after go into the year-by-year projection, as factors
 *     RELATIVE TO YEAR 1, so the projection's first year is exactly 1.0 and the
 *     first-year drop is not charged twice.
 */

export const MIN_RETENTION_PCT = 60.0;
export const MAX_RETENTION_PCT = 100.0;
export const MAX_YEARS = 60;

function num(value) {
  if (typeof value === "boolean") return null;      // true is not 1% of anything
  if (value === null || value === undefined || value === "") return null;
  const out = Number(String(value).replace(",", "."));
  return Number.isFinite(out) ? out : null;
}

/**
 * The table a datasheet's two headline numbers imply.
 *
 * Not the stored form -- the stored form is the table, which is edited row by
 * row -- but the thing that fills it, so nobody types thirty rows by hand to
 * describe a curve the datasheet states in one line.
 */
export function linearRetention(firstYearPct, annualPct, years) {
  const first = Math.max(0, num(firstYearPct) ?? 0);
  const annual = Math.max(0, num(annualPct) ?? 0);
  const count = Math.max(0, Math.min(Math.trunc(num(years) ?? 0), MAX_YEARS));
  return Array.from({ length: count }, (_, index) =>
    Number((100.0 - first - annual * index).toFixed(4))
  );
}

/**
 * Everything wrong with a table, all at once.
 *
 * All at once because a form that reports one problem, is corrected, and then
 * reports the next is a form that gets abandoned half-corrected.
 */
export function validate(retention) {
  if (!Array.isArray(retention) || retention.length === 0) {
    return [{ year: null, message: "The degradation curve has no rows." }];
  }
  const problems = [];
  if (retention.length > MAX_YEARS) {
    problems.push({ year: null, message: `A curve cannot describe more than ${MAX_YEARS} years.` });
  }
  let previous = null;
  retention.slice(0, MAX_YEARS).forEach((raw, index) => {
    const year = index + 1;
    const value = num(raw);
    if (value === null) {
      problems.push({ year, message: "Not a number." });
      return;
    }
    if (value < MIN_RETENTION_PCT || value > MAX_RETENTION_PCT) {
      problems.push({
        year,
        message: `Retention must be between ${MIN_RETENTION_PCT}% and ${MAX_RETENTION_PCT}% of nameplate.`,
      });
    } else if (previous !== null && value > previous + 1e-9) {
      // Modules do not recover. A row above the one before it is almost always
      // two rows swapped, which a warranty check would NOT catch because the
      // endpoint can still land correctly.
      problems.push({
        year,
        message: `Retention rises above year ${year - 1}; a module does not recover.`,
      });
    }
    previous = value;
  });
  return problems;
}

/** The table as numbers, or null when it cannot be read. Never repaired. */
export function curveFrom(retention) {
  const values = Array.isArray(retention) ? retention : [];
  if (validate(values).length) return null;
  return values.slice(0, MAX_YEARS).map((value) => num(value));
}

/** What the first year costs, which the hourly run books as LID. */
export function firstYearLossPct(retention) {
  const curve = curveFrom(retention);
  if (!curve) return null;
  // Never negative: a negative DC loss handed to pvlib would be a GAIN applied
  // to every hour of the year.
  return Math.max(0, 100.0 - curve[0]);
}

/**
 * Year-by-year factors for the financial projection, relative to year 1.
 *
 * Past the end of the table the curve continues at its OWN last annual step
 * rather than flat: a 25-year warranty on a 30-year project says nothing about
 * years 26-30, and holding it flat would credit the plant with five years of a
 * module that has stopped ageing.
 */
export function projectionFactors(retention, years) {
  const curve = curveFrom(retention);
  const wanted = Math.max(0, Math.trunc(num(years) ?? 0));
  if (!curve || wanted === 0) return [];
  const series = curve.slice();
  if (series.length === 1) {
    while (series.length < wanted) series.push(series[0]);
  }
  while (series.length < wanted) {
    const step = series[series.length - 1] - series[series.length - 2];
    series.push(Math.max(0, series[series.length - 1] + step));
  }
  const base = series[0];
  if (!base) return new Array(wanted).fill(1);
  return series.slice(0, wanted).map((value) => value / base);
}

/**
 * The curve in one line, for a summary row and for the read-only view.
 *
 * Reads the shape back OUT of the table rather than out of the seed fields,
 * because the table is what the engine uses: a row edited by hand after the
 * table was filled would otherwise be described by numbers it no longer has.
 */
export function describe(retention) {
  const curve = curveFrom(retention);
  if (!curve || !curve.length) return null;
  const first = Math.max(0, 100 - curve[0]);
  const steps = curve.slice(1).map((value, index) => curve[index] - value);
  const annual = steps.length
    ? steps.reduce((total, step) => total + step, 0) / steps.length
    : 0;
  const uniform =
    steps.length < 2 || steps.every((step) => Math.abs(step - steps[0]) < 5e-4);
  return {
    years: curve.length,
    firstYearPct: first,
    annualPct: annual,
    finalRetentionPct: curve[curve.length - 1],
    // A hand-edited table is not describable by two numbers, and saying so is
    // the difference between a summary and a claim.
    uniform,
  };
}
